import { useEffect, useState } from 'react';
import { payConfigGateway } from '../../../pay-config';
import { PayDaySelectors } from '../../../pay-config/PayDaySelectors';
import {
  EMPTY_PAY_DAY_SELECTION,
  PAY_FREQUENCY_DISPLAY,
  formatPaySchedule,
  frequencyKey,
  isOutsideStandardOptions,
  selectionFromSchedule,
  selectionToPayload,
  validatePayDaySelection,
  type PayDayField,
  type PayDaySelection,
} from '../../../pay-config/paySchedule';
import { payFrequencyLabels, type PayScheduleResponse } from '../../../pay-config/types';
import { peopleGateway } from '../../index';
import type { EmployeePayScheduleResponse, EmployeeResponse } from '../../types';

/** The "+ New schedule for this employee" choice in the Pay Schedule list. */
const NEW_SCHEDULE = '__new__';

/** Frequencies the backend can store (1 to 5). Annually has no backend frequency yet (BACKEND GAP / payroll SME), so it is not offered. */
const OFFERED_FREQUENCIES = Object.entries(payFrequencyLabels).map(([value, label]) => ({ value, label }));

/** Pay Information (EPN-192, Figma admin screens 13 to 16). The employee uses the company's default pay schedule unless an authorized admin turns on
 * "Use a different pay schedule for this employee". Off: the company schedule is shown read-only ("Using company pay schedule"). On: the admin
 * picks another company schedule, or creates one for this employee from a frequency, pay days and a pay period start date. Either way the employee
 * is only ever ASSIGNED to a PayrollConfig schedule (effective-dated, append-only); Employees never copies schedule values, so a schedule keeps
 * one owner. Turning the checkbox off again just shows the company schedule: nothing is deleted until a deliberate save, and the old assignment
 * stays in history. BACKEND GAP: a schedule stores one pay period start date, so the Figma "Second Pay Period Start Date" is shown but cannot
 * be entered; pay days and start dates are separate concepts here and are never mixed up. */
export function EmployeePayScheduleSection({ employee, onSaved }: { employee: EmployeeResponse; onSaved?: (() => void) | undefined }) {
  const [assignment, setAssignment] = useState<EmployeePayScheduleResponse | null>(null);
  const [schedules, setSchedules] = useState<PayScheduleResponse[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [override, setOverride] = useState(false);
  const [choice, setChoice] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [newFrequency, setNewFrequency] = useState('');
  const [newSelection, setNewSelection] = useState<PayDaySelection>(EMPTY_PAY_DAY_SELECTION);
  const [newStartDate, setNewStartDate] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<PayDayField | 'frequency', string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([peopleGateway.getEmployeePaySchedule(employee.id), payConfigGateway.listPaySchedules()])
      .then(([a, list]) => {
        if (cancelled) return;
        const def = list.find((s) => s.isDefault && s.isActive) ?? null;
        const assignedId = a.current?.payScheduleId ?? null;
        const overriding = assignedId !== null && def !== null && assignedId !== def.id;
        setAssignment(a);
        setSchedules(list);
        setOverride(overriding);
        setChoice(overriding ? assignedId : '');
        setEffectiveDate('');
        setNewFrequency('');
        setNewSelection(EMPTY_PAY_DAY_SELECTION);
        setNewStartDate('');
        setFieldErrors({});
        setLoadError(null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Could not load the pay information.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id, reloadKey]);

  function retry() {
    setLoading(true);
    setLoadError(null);
    setReloadKey((k) => k + 1);
  }

  const title = <h3 className="profile-section-title">Pay Information</h3>;

  if (loading) {
    return (
      <section className="profile-section">
        {title}
        <p className="muted" role="status">Loading pay information…</p>
      </section>
    );
  }
  if (loadError || !assignment) {
    return (
      <section className="profile-section">
        {title}
        <p className="error" role="alert">{loadError ?? 'Could not load the pay information.'}</p>
        <button type="button" onClick={retry}>Retry</button>
      </section>
    );
  }

  const defaultSchedule = schedules.find((s) => s.isDefault && s.isActive) ?? null;
  const assignedId = assignment.current?.payScheduleId ?? null;
  const hasAssignment = assignment.history.length > 0;
  const latestDate = assignment.history[0]?.effectiveFrom ?? null;
  const creatingNew = override && choice === NEW_SCHEDULE;
  const choices = schedules.filter((s) => s.isActive || s.id === assignedId);

  // The schedule whose values are on screen: the company's when the override is off, the picked one when it is on.
  const shown = override ? (creatingNew ? null : schedules.find((s) => s.id === choice) ?? null) : defaultSchedule;
  const shownKey = shown ? frequencyKey(shown.frequency) : null;
  const shownSelection = shown ? selectionFromSchedule(shown) : null;
  const shownSummary = shown ? formatPaySchedule(shown) : '';

  const newKey = frequencyKey(newFrequency === '' ? null : Number(newFrequency));
  const targetId = override ? (creatingNew ? null : choice || null) : defaultSchedule?.id ?? null;
  const isChange = creatingNew || (targetId !== null && targetId !== assignedId);
  const needsEffectiveDate = hasAssignment && isChange;
  const futureChange = assignment.history.some((h) => !h.isCurrent && h.effectiveFrom > (assignment.current?.effectiveFrom ?? ''));
  const currentName = schedules.find((s) => s.id === assignedId)?.name ?? 'a pay schedule that is no longer listed';
  const semimonthly = (creatingNew ? newKey : shownKey) === 'semimonthly';

  function toggleOverride(on: boolean) {
    setOverride(on);
    setSaved(false);
    setError(null);
    setFieldErrors({});
    // Off again: back to the company schedule on screen. Nothing already saved is touched.
    setChoice(on && assignedId && assignedId !== defaultSchedule?.id ? assignedId : '');
  }

  async function save() {
    setError(null);
    setSaved(false);
    setFieldErrors({});
    let scheduleId = targetId;
    if (!override && !defaultSchedule) {
      setError('Your company has no default pay schedule. Set one under Pay Schedules first.');
      return;
    }
    if (override && !creatingNew && !scheduleId) {
      setError('Please choose a pay schedule.');
      return;
    }
    if (needsEffectiveDate && effectiveDate === '') {
      setError('Please enter an effective date for the pay schedule change before saving.');
      return;
    }
    if (creatingNew) {
      const errors: Partial<Record<PayDayField | 'frequency', string>> = {};
      if (!newKey) errors.frequency = 'Pay Frequency is required.';
      else Object.assign(errors, validatePayDaySelection(newKey, newSelection));
      if (Object.keys(errors).length > 0 || !newKey || newKey === 'annually') {
        setFieldErrors(errors);
        setError('Complete the required pay schedule fields before saving.');
        return;
      }
    }
    setSaving(true);
    try {
      if (creatingNew && newKey && newKey !== 'annually') {
        const who = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || 'employee';
        const created = await payConfigGateway.createPaySchedule({
          name: `${PAY_FREQUENCY_DISPLAY[newKey]} - ${who}`.slice(0, 100),
          frequency: Number(newFrequency),
          ...selectionToPayload(newKey, newSelection),
          payPeriodStartDate: newStartDate || null,
        });
        scheduleId = created.id;
        setSchedules((list) => [...list, created]);
      }
      const r = await peopleGateway.saveEmployeePaySchedule(employee.id, { payScheduleId: scheduleId!, effectiveDate: effectiveDate || null });
      setAssignment(r);
      const nowOverriding = defaultSchedule !== null && r.current?.payScheduleId !== defaultSchedule.id;
      setOverride(nowOverriding);
      setChoice(nowOverriding ? (r.current?.payScheduleId ?? '') : '');
      setEffectiveDate('');
      setNewFrequency('');
      setNewSelection(EMPTY_PAY_DAY_SELECTION);
      setNewStartDate('');
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the pay schedule.');
    } finally {
      setSaving(false);
    }
  }

  const readOnly = !override;
  return (
    <section className="profile-section">
      {title}
      <p className="muted">
        Payroll information is initially set during Business Profile setup. To update this employee&apos;s pay information, select
        the checkbox and complete the required fields.
      </p>
      {schedules.length === 0 && (
        <p className="muted" role="status">Your company has no pay schedules yet. Add one under Pay Schedules, then assign it here.</p>
      )}
      {readOnly && defaultSchedule && (
        <p role="status" data-testid="using-company-schedule">
          <strong>Using company pay schedule:</strong> {defaultSchedule.name}
        </p>
      )}
      {readOnly && !defaultSchedule && schedules.length > 0 && (
        <p className="muted" role="status">Your company has no default pay schedule. Set one under Pay Schedules, or use a different schedule for this employee.</p>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={override} onChange={(e) => toggleOverride(e.target.checked)} />
        Use a different pay schedule for this employee <span aria-hidden="true">*</span>
      </label>

      <div className="profile-field-grid" style={{ marginTop: 8 }}>
        <label>
          Pay Schedule <span aria-hidden="true">*</span>
          <select
            value={readOnly ? (defaultSchedule?.id ?? '') : choice}
            disabled={readOnly}
            onChange={(e) => { setChoice(e.target.value); setSaved(false); setError(null); }}
          >
            <option value="">{readOnly ? 'No default pay schedule' : 'Select a pay schedule'}</option>
            {(readOnly ? (defaultSchedule ? [defaultSchedule] : []) : choices).map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({payFrequencyLabels[s.frequency] ?? s.frequency})</option>
            ))}
            {!readOnly && <option value={NEW_SCHEDULE}>+ New schedule for this employee</option>}
          </select>
        </label>

        {creatingNew && (
          <label>
            Pay Frequency <span aria-hidden="true">*</span>
            <select
              value={newFrequency}
              aria-invalid={fieldErrors.frequency ? true : undefined}
              onChange={(e) => { setNewFrequency(e.target.value); setNewSelection(EMPTY_PAY_DAY_SELECTION); setSaved(false); }}
            >
              <option value="">Select a pay frequency</option>
              {OFFERED_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            {fieldErrors.frequency && <span className="error" role="alert">{fieldErrors.frequency}</span>}
          </label>
        )}

        <label>
          Pay Period Start Date
          <input
            type="date"
            value={creatingNew ? newStartDate : (shown?.payPeriodStartDate ?? '')}
            readOnly={!creatingNew}
            disabled={!creatingNew}
            placeholder={creatingNew ? undefined : 'Set on the pay schedule'}
            onChange={(e) => { setNewStartDate(e.target.value); setSaved(false); }}
          />
        </label>
        {semimonthly && (
          <label>
            Second Pay Period Start Date
            <input type="date" value="" readOnly disabled aria-describedby="second-start-note" />
          </label>
        )}

        {creatingNew ? (
          newKey && newKey !== 'annually' ? (
            <PayDaySelectors
              frequency={newKey}
              value={newSelection}
              errors={fieldErrors}
              idPrefix="employee-new-payday"
              onChange={(field, value) => { setNewSelection((s) => ({ ...s, [field]: value })); setSaved(false); }}
            />
          ) : (
            <label>Pay Day<input readOnly disabled value="" placeholder="Choose a pay frequency first" /></label>
          )
        ) : shownKey && shownSelection ? (
          <PayDaySelectors frequency={shownKey} value={shownSelection} idPrefix="employee-payday" />
        ) : (
          <label>Pay Day<input readOnly disabled value="" placeholder="Set on the pay schedule" /></label>
        )}

        {needsEffectiveDate || !hasAssignment ? (
          isChange && (
            <label>
              {hasAssignment ? 'Effective Date *' : 'Effective Date (defaults to the hiring date)'}
              <input
                type="date"
                value={effectiveDate}
                min={latestDate ?? undefined}
                onChange={(e) => { setEffectiveDate(e.target.value); setSaved(false); }}
              />
            </label>
          )
        ) : null}
      </div>
      {semimonthly && (
        <p id="second-start-note" className="muted" data-testid="second-start-note">
          <strong>Needs payroll confirmation:</strong> a pay schedule stores one pay period start date, so a second start date for a twice-a-month
          schedule cannot be entered yet. Pay days and pay period start dates are different things and are never swapped.
        </p>
      )}
      {creatingNew && (
        <p className="muted">This creates a pay schedule for your company and assigns this employee to it. Annual pay schedules are not available yet.</p>
      )}
      {shownSummary && <p className="muted" data-testid="pay-schedule-summary">Pay schedule: {shownSummary}.</p>}
      {shownKey && shownSelection && isOutsideStandardOptions(shownKey, shownSelection) && (
        <p className="muted" role="note">
          This schedule&apos;s pay day is outside the standard Pay Day options. Review it on the Pay Schedules screen.
        </p>
      )}
      {assignment.current && (
        <p className="muted" style={{ marginTop: 10 }}>
          Current: {currentName}, effective {assignment.current.effectiveFrom}.
        </p>
      )}
      {futureChange && <p className="muted">A later pay schedule change is already on file and will apply from its effective date.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="muted" role="status">Pay information saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving || (!isChange && !creatingNew)}>
          {saving ? 'Saving…' : 'Save Pay Information'}
        </button>
      </div>
    </section>
  );
}
